import type { MeetingPlanTemplateFormData } from './validation';
import type {
    EditableMeetingTemplate,
    MeetingTemplateUpdateRequest,
} from '@/types/thematic-meetings';
import type { ContentLanguage } from '@/utils/contentLanguage';
import type { LocalizedText } from '@/utils/localization';

/**
 * Reads the one language the editor writes in. Deliberately not `getTextByLanguage`, which falls back
 * to another language when the asked-for one is missing: the editor must show an empty field for a
 * wording this company has never written, or saving would copy the other language over it.
 */
const wordingIn = (text: LocalizedText, language: ContentLanguage): string => text[language] ?? '';

/** The stored wording with only <paramref>language</paramref> retyped; every other language survives. */
const retyped = (
    stored: LocalizedText | null | undefined,
    language: ContentLanguage,
    wording: string,
): LocalizedText => ({ ...stored, [language]: wording });

/** A plain bilingual list, as the form edits it: one language typed, the others carried along. */
const toTextList = (entries: LocalizedText[], language: ContentLanguage) =>
    entries.map((entry) => ({ text: wordingIn(entry, language), storedText: entry }));

const fromTextList = (
    entries: MeetingPlanTemplateFormData['tips'],
    language: ContentLanguage,
): LocalizedText[] => entries.map((entry) => retyped(entry.storedText, language, entry.text));

export const toFormData = (
    template: EditableMeetingTemplate,
    language: ContentLanguage,
): MeetingPlanTemplateFormData => ({
    isActive: template.isActive,
    title: wordingIn(template.title, language),
    objective: wordingIn(template.objective, language),
    invitationText: wordingIn(template.invitationText, language),
    openingStatementTitle: wordingIn(template.openingStatement.title, language),
    openingStatementText: wordingIn(template.openingStatement.text, language),
    agendaItems: template.agendaItems.map((item) => ({
        title: wordingIn(item.title, language),
        durationMinutes: item.durationMinutes,
        description: wordingIn(item.description, language),
        storedText: { title: item.title, description: item.description },
        discussionPoints: item.discussionPoints,
    })),
    preparationQuestions: toTextList(template.preparationQuestions, language),
    tips: toTextList(template.tips, language),
    observations: toTextList(template.observations, language),
    conversationTips: template.conversationTips.map((tip) => ({
        title: wordingIn(tip.title, language),
        description: wordingIn(tip.description, language),
        storedText: { title: tip.title, description: tip.description },
    })),
});

export const toUpdateRequest = (
    template: EditableMeetingTemplate,
    form: MeetingPlanTemplateFormData,
    language: ContentLanguage,
): MeetingTemplateUpdateRequest => ({
    isActive: form.isActive,
    title: retyped(template.title, language, form.title),
    objective: retyped(template.objective, language, form.objective),
    // Not part of the form: the editor never shows Focus (it plays no part in a meeting), so the
    // update — a whole-object replacement on the server — sends the plan's existing wording back
    // untouched rather than losing it.
    focus: template.focus,
    invitationText: retyped(template.invitationText, language, form.invitationText),
    openingStatement: {
        title: retyped(template.openingStatement.title, language, form.openingStatementTitle),
        text: retyped(template.openingStatement.text, language, form.openingStatementText),
    },
    agendaItems: form.agendaItems.map((item) => ({
        title: retyped(item.storedText?.title, language, item.title),
        durationMinutes: item.durationMinutes,
        description: retyped(item.storedText?.description, language, item.description),
        discussionPoints: item.discussionPoints,
    })),
    preparationQuestions: fromTextList(form.preparationQuestions, language),
    tips: fromTextList(form.tips, language),
    observations: fromTextList(form.observations, language),
    conversationTips: form.conversationTips.map((tip) => ({
        title: retyped(tip.storedText?.title, language, tip.title),
        description: retyped(tip.storedText?.description, language, tip.description),
    })),
});
